import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useOrdersQuery, useStatusQuery } from "../../Store/Apis/bookingApi";
import { useOutletsQuery } from "../../Store/Apis/outletApi";
import Loader from "../Loader/Loader";
import Error from "../Error/Error";
import getStatusColor from "../../Hooks/GetStatusColor";
import { Table } from "antd";
import formatDate from "../../Hooks/formatDate";
import { isPermissionGranted } from "../../Hooks/permissionsMiddleware";

const CreditNoteList = () => {
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const statusFromUrl = urlParams.get("status");

  const [sortFilter, setSortFilter] = useState({
    search: "",
    outlet: "",
    status: statusFromUrl || "",
    technicianID: "",
  });

  const bookingApi = useOrdersQuery({
    keyword: sortFilter.search,
    statusName: sortFilter.status,
    outletID: sortFilter.outlet,
    technicianID: sortFilter.technicianID,
    exceeded_limit: "no", // For credit note list we show normal orders baseline
    statusID: 10, // Show only items with status ID 10 instead of status ID 7
  });

  const outletList = useOutletsQuery();
  const statusList = useStatusQuery();

  const columns = [
    {
      title: "Order Info",
      dataIndex: "orderInfo",
      fixed: "left",
      width: 250,
      render: (_, record) => (
        <>
          <p className="text-sm mb-0">
            <span className="me-2">Order ID:</span>
            <span className="font-weight-bold">{record.orderNumber}</span>
          </p>
          <p className="text-sm mb-0">
            <span className="me-2">Check In Date:</span>
            <span>{formatDate(record.orderDate)}</span>
          </p>
          <p className="text-sm mb-0">
            <span className="me-2">Check Out Date:</span>
            <span>{formatDate(record.checkOutDate)}</span>
          </p>
        </>
      ),
    },
    // {
    //   title: "Outlet",
    //   dataIndex: "outletName",
    // },
    {
      title: "Customer",
      dataIndex: "customer",
      render: (_, record) => (
        <>
          <p className="text-sm font-weight-bold mb-0">{record.customerName}</p>
          <p className="text-sm mb-0">{record.customerNumber}</p>
        </>
      ),
    },
    {
      title: "Balance Owed",
      dataIndex: "balanceOwed",
      render: (_, record) => {
        // Business-centric definition: What WE owe the customer due to overpayment.
        // Overpayment base = amountPaid - order subTotal (from API, BEFORE credits applied)
        // Remaining owed = overpayment - creditedAmountAlreadyIssued (cannot go below 0)
        const amountPaid = Number(record.amountPaid) || 0;
        const orderAmount =
          Number(record.originalSubTotal) || Number(record.subTotal) || 0; // prefer preserved original
        const credited = Number(record.creditedAmount) || 0;

        // If no payment made, nothing is owed back to customer.
        if (amountPaid === 0)
          return <span className="font-weight-bold text-danger">- R 0.00</span>;

        const overPayment = amountPaid - orderAmount;
        if (overPayment <= 0)
          return <span className="font-weight-bold text-danger">- R 0.00</span>; // no overpayment => nothing owed

        const remainingOwed = Math.max(overPayment - credited, 0);
        return (
          <span className="font-weight-bold text-danger">
            - R {remainingOwed.toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Order Amount",
      dataIndex: "subTotal",
      render: (subTotal) => `R ${subTotal}`,
    },
    {
      title: "Status",
      dataIndex: "statusDescription",
      render: (status, record) => (
        <>
          <span
            className={`badge badge-sm text-uppercase badge-${getStatusColor(
              status
            )}`}
            style={{ cursor: "default" }}
          >
            {status}
          </span>
        </>
      ),
    },
    {
      title: "Payment",
      dataIndex: "payment",
      render: (_, record) => (
        <>
          <p className="text-sm mb-0">
            <span className="me-2">Total Amount (Net):</span>
            <span className="font-weight-bold">R {record.subTotal}</span>
          </p>
          <p className="text-sm mb-0">
            <span className="me-2">Credit Amount:</span>
            <span className="font-weight-bold text-danger">
              R {record.creditedAmount}
            </span>
          </p>

          <p className="text-sm mb-0">
            <span className="me-2">Paid Amount:</span>
            <span className="font-weight-bold">R {record.amountPaid}</span>
          </p>
        </>
      ),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      render: (_, record) => (
        <p className="text-sm mb-0">{record.createdByName}</p>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <>
          {isPermissionGranted(2) && (
            <Link
              to={`/credit/credit-note/${record.orderId}`}
              className="badge badge-xs badge-primary text-xs fw-600 mx-1 mb-1"
            >
              View
            </Link>
          )}
        </>
      ),
    },
  ];

  // Transform data to fit the Ant Design table structure (copying logic from SalesHistory)
  // Filter to only show items with statusID 10
  const dataSource = (bookingApi?.data?.data || [])
    .filter((elem) => {
      // Check both possible field names for the status ID
      const statusId =
        elem.statusID || elem.statusId || elem.StatusID || elem.status_id;
      return statusId === 10;
    })
    .map((elem, index) => {
      // Sum credited amounts from nested serviceTypeAndName array if present
      const lineCreditedTotal = Array.isArray(elem.serviceTypeAndName)
        ? elem.serviceTypeAndName.reduce(
            (acc, item) => acc + (Number(item.creditedAmount) || 0),
            0
          )
        : 0;
      // Prefer top-level creditedAmount if it's a valid number and > 0, else use the summed line items
      const effectiveCreditedAmount =
        typeof elem.creditedAmount === "number" &&
        !isNaN(elem.creditedAmount) &&
        elem.creditedAmount > 0
          ? elem.creditedAmount
          : lineCreditedTotal;
      // Preserve the API's original subTotal (order amount before any credits). We do NOT subtract credits here.
      const originalSubTotal = Number(elem.subTotal || 0);
      return {
        key: index,
        orderNumber: elem.orderNumber,
        orderDate: elem.orderDate,
        checkOutDate: elem.checkOutDate,
        outletName: elem.outletName,
        outletId: elem.outletId ?? elem.outletID ?? user?.outletID,
        customerName: elem.customerName,
        customerNumber: elem.customerNumber,
        // Show order amount (original) in Order Amount column (unchanged by credits)
        subTotal: originalSubTotal.toFixed(2),
        originalSubTotal: originalSubTotal.toFixed(2),
        statusDescription: elem.statusDescription,
        statusID:
          elem.statusID || elem.statusId || elem.StatusID || elem.status_id, // Add statusID to the data source for debugging
        amountPaid: Number(elem.amountPaid).toFixed(2),
        totalAmount: Number(elem.totalAmount).toFixed(2),
        creditedAmount: Number(effectiveCreditedAmount || 0).toFixed(2),
        orderId: elem.orderId,
        createdBy: elem.createdBy,
        createdByName: elem.createdByName,
      };
    });

  useEffect(() => {
    if (user.role !== "superAdmin") {
      setSortFilter((prev) => ({
        ...prev,
        outlet: user.outletID,
        status: statusFromUrl || prev.status,
      }));
    }
  }, [statusFromUrl]);

  // Debug effect to log API response for troubleshooting
  useEffect(() => {
    if (bookingApi.isSuccess) {
      console.log("API Response:", bookingApi.data.data);
      console.log(
        "Status IDs in response:",
        bookingApi.data.data.map(
          (item) => `${item.orderNumber}: ${item.statusID || "unknown"}`
        )
      );
    }
  }, [bookingApi.isSuccess, bookingApi.data]);

  return (
    <div>
      <div className="row align-items-center justify-content-between mb-4">
        <div className="col">
          <h5 className="fw-500 text-white mb-0">
            Credit Note Orders (Status ID: 10)
          </h5>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header p-4">
              <div className="row">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by Order Id, Outlet and Customer"
                    value={sortFilter?.search || ""}
                    onChange={(e) =>
                      setSortFilter({ ...sortFilter, search: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    disabled={!outletList.isSuccess || user.role_Id !== 10}
                    value={sortFilter?.outlet}
                    onChange={(e) =>
                      setSortFilter({ ...sortFilter, outlet: e.target.value })
                    }
                  >
                    <option className="select-box" value="">
                      All Outlets
                    </option>
                    {outletList?.data?.data.map((elem) => (
                      <option value={elem.outletId}>{elem.outletName}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={sortFilter?.status || ""}
                    onChange={(e) =>
                      setSortFilter({ ...sortFilter, status: e.target.value })
                    }
                  >
                    <option className="select-box" value="">
                      All Bookings
                    </option>
                    {statusList?.data?.data.map((elem) => (
                      <option className="select-box" value={elem.description}>
                        {elem.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              {bookingApi.isError && <Error />}
              {bookingApi.isLoading && <Loader height="500px" />}
              {bookingApi.isSuccess && (
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  pagination={{ pageSize: 10 }}
                  className="mx-4"
                  scroll={{ x: 1500 }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditNoteList;
